import React, { useState } from "react";
import { useTasks, useCourses } from "../hooks/useStudyOS";
import { toggleTask, deleteTask, editTask, addTask } from "../lib/db";

export default function TasksScreen() {
  const { tasks } = useTasks();
  const { getCourse, courses } = useCourses();
  const [filter, setFilter] = useState("all");
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [completingTask, setCompletingTask] = useState(null);
  const [completionNote, setCompletionNote] = useState("");
  const [completionError, setCompletionError] = useState("");
  const [noSessionWarning, setNoSessionWarning] = useState(false);

  // ── Countdown helper ──
  const getCountdown = (due) => {
  if (!due) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let dueDate;

  // Handle exact dates (YYYY-MM-DD format)
  if (due.includes("-")) {
    const [year, month, day] = due.split("-");
    dueDate = new Date(year, month - 1, day);
  } else if (due === "Today") {
    dueDate = today;
  } else if (due === "Tomorrow") {
    dueDate = new Date(today.getTime() + 86400000);
  } else if (due === "This week") {
    dueDate = new Date(today.getTime() + 7 * 86400000);
  } else if (due === "Next week") {
    dueDate = new Date(today.getTime() + 14 * 86400000);
  } else {
    return null;
  }

  const diff = dueDate.getTime() - today.getTime();
  const days = Math.floor(diff / 86400000);

  if (days < 0) return { label: "Overdue!", color: "#F05D5D", bg: "rgba(240,93,93,.15)" };
  if (days === 0) return { label: "Due today", color: "#F05D5D", bg: "rgba(240,93,93,.15)" };
  if (days === 1) return { label: "Due tomorrow", color: "#F0A429", bg: "rgba(240,164,41,.15)" };
  if (days <= 3) return { label: `${days} days left`, color: "#F0A429", bg: "rgba(240,164,41,.15)" };
  return { label: `${days} days left`, color: "#2ECC8A", bg: "rgba(46,204,138,.15)" };
};

  const filtered = tasks.filter(t => {
    if (filter === "pending") return !t.done;
    if (filter === "done") return t.done;
    return true;
  });

  const high   = filtered.filter(t => !t.done && t.priority === "high");
  const normal = filtered.filter(t => !t.done && t.priority !== "high");
  const done   = filtered.filter(t => t.done);

  // ── Handle complete with gate ──
  const handleComplete = (task) => {
  if (task.done) {
    toggleTask(task.id, task.done);
    return;
  }
  setCompletingTask(task);
  setCompletionNote("");
  setCompletionError("");
  setNoSessionWarning(task.type === "deep" && !task.hasSession);
};

  const submitCompletion = async () => {
    if (completionNote.trim().length < 30) {
      setCompletionError("Please write at least 30 characters describing what you completed.");
      return;
    }
    await editTask(completingTask.id, {
      done: true,
      doneAt: new Date().toISOString(),
      completionNote: completionNote.trim(),
    });
    setCompletingTask(null);
    setCompletionNote("");
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setEditForm({
      name: task.name,
      courseId: task.courseId || "",
      priority: task.priority || "normal",
      due: task.due || "",
      type: task.type || "quick",
      estimatedMins: task.estimatedMins || "",
    });
  };

  const saveEdit = async () => {
    if (!editForm.name) return;
    await editTask(editingTask.id, {
      name: editForm.name,
      courseId: editForm.courseId || null,
      priority: editForm.priority,
      due: editForm.due || null,
      type: editForm.type,
      estimatedMins: editForm.estimatedMins ? parseInt(editForm.estimatedMins) : null,
    });
    setEditingTask(null);
  };

  const handleAddTask = async () => {
    if (!addForm.name) return;
    await addTask({
      name: addForm.name,
      courseId: addForm.courseId || null,
      priority: addForm.priority || "normal",
      due: addForm.due || null,
      type: addForm.type || "quick",
      estimatedMins: addForm.estimatedMins ? parseInt(addForm.estimatedMins) : null,
    });
    setAddForm({});
    setShowAddModal(false);
  };

  const TaskCard = ({ t }) => {
    const course = getCourse(t.courseId);
    const countdown = getCountdown(t.due);
    const taskType = t.type || "quick";

    return (
      <div style={styles.taskCard}>
        <div
          style={{...styles.check, ...(t.done ? styles.checkDone : {})}}
          onClick={() => handleComplete(t)}>
          {t.done && <span style={{fontSize:"10px",color:"#fff"}}>✓</span>}
        </div>
        <div style={styles.taskBody}>
          <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
            <div style={{...styles.taskName, ...(t.done ? styles.taskDone : {})}}>{t.name}</div>
            <span style={{...styles.typeBadge, ...(taskType === "deep" ? styles.typeBadgeDeep : styles.typeBadgeQuick)}}>
              {taskType === "deep" ? "🎯 Deep" : "⚡ Quick"}
            </span>
          </div>
          {course && <div style={{...styles.taskCourse, color: course.color}}>{course.name}</div>}
          {taskType === "deep" && t.estimatedMins && (
            <div style={styles.timeEstimate}>⏱ Est. {t.estimatedMins} mins</div>
          )}
          {t.done && t.completionNote && (
            <div style={styles.completionNote}>✅ {t.completionNote}</div>
          )}
          {countdown && !t.done && (
            <div style={{...styles.countdown, color: countdown.color, background: countdown.bg}}>
              {countdown.label}
            </div>
          )}
          
        </div>
        <div style={styles.taskRight}>
          {!t.done && (
            <button style={styles.editBtn} onClick={() => handleEdit(t)}>✏️</button>
          )}
          <button style={styles.delBtn} onClick={() => deleteTask(t.id)}>×</button>
        </div>
      </div>
    );
  };

  const modalForm = (form, setForm, onSubmit, title) => (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && (setEditingTask(null) || setShowAddModal(false))}>
      <div style={styles.modalBox}>
        <div style={styles.handle}/>
        <div style={styles.modalTitle}>{title}</div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Task name *</label>
          <input style={styles.input} value={form.name||""} placeholder="e.g. Submit assignment"
            onChange={e => setForm({...form, name: e.target.value})}/>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Task type</label>
          <div style={{display:"flex", gap:"8px"}}>
            <button style={{...styles.typeBtn, ...(form.type==="quick" ? styles.typeBtnActive : {})}}
              onClick={() => setForm({...form, type:"quick"})}>⚡ Quick task</button>
            <button style={{...styles.typeBtn, ...(form.type==="deep" ? styles.typeBtnActiveDeep : {})}}
              onClick={() => setForm({...form, type:"deep"})}>🎯 Deep work</button>
          </div>
        </div>
        {form.type === "deep" && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Estimated time (minutes)</label>
            <input style={styles.input} type="number" placeholder="e.g. 60"
              value={form.estimatedMins||""}
              onChange={e => setForm({...form, estimatedMins: e.target.value})}/>
          </div>
        )}
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Course</label>
            <select style={styles.input} value={form.courseId||""}
              onChange={e => setForm({...form, courseId: e.target.value})}>
              <option value="">General</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Priority</label>
            <select style={styles.input} value={form.priority||"normal"}
              onChange={e => setForm({...form, priority: e.target.value})}>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Due date</label>
          <input
  style={styles.input}
  type="date"
  value={form.due||""}
  min={new Date().toISOString().split("T")[0]}
  onChange={e => setForm({...form, due: e.target.value})}
/>
        </div>
        <button style={styles.submitBtn} onClick={onSubmit}>Save</button>
      </div>
    </div>
  );

  return (
    <div className="screen-wrap">
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px"}}>
        <div style={styles.tabs}>
          {["all","pending","done"].map(f => (
            <button key={f} style={{...styles.tab, ...(filter===f ? styles.tabActive : {})}}
              onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <button style={styles.addBtn} onClick={() => { setShowAddModal(true); setAddForm({type:"quick"}); }}>+ Add</button>
      </div>

      {tasks.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>✅</div>
          <div style={styles.emptyTitle}>No tasks yet</div>
          <div style={styles.emptyDesc}>Tap "+ Add" to create your first task.</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>🎉</div>
          <div style={styles.emptyTitle}>Nothing here!</div>
          <div style={styles.emptyDesc}>{filter === "pending" ? "All tasks are done!" : "No completed tasks yet."}</div>
        </div>
      ) : (
        <>
          {high.length > 0 && <><div style={styles.sLabel}>High priority</div>{high.map(t => <TaskCard key={t.id} t={t}/>)}</>}
          {normal.length > 0 && <><div style={styles.sLabel}>Other tasks</div>{normal.map(t => <TaskCard key={t.id} t={t}/>)}</>}
          {done.length > 0 && <><div style={styles.sLabel}>Completed</div>{done.map(t => <TaskCard key={t.id} t={t}/>)}</>}
        </>
      )}

      {/* Edit modal */}
      {editingTask && modalForm(editForm, setEditForm, saveEdit, "Edit task")}

      {/* Add modal */}
      {showAddModal && modalForm(addForm, setAddForm, handleAddTask, "Add task")}

      {/* Completion gate modal */}
      {completingTask && (
  <div style={styles.overlay} onClick={e => e.target === e.currentTarget && setCompletingTask(null)}>
    <div style={styles.modalBox}>
      <div style={styles.handle}/>
      <div style={styles.modalTitle}>Mark as complete ✅</div>

      {noSessionWarning && (
        <div style={{background:"var(--amber-bg)", border:"1px solid rgba(240,164,41,.2)", borderRadius:"10px", padding:"12px 14px", marginBottom:"16px", fontSize:"13px", color:"var(--amber)", lineHeight:1.5}}>
          ⚠️ No focus session logged for this task. For real productivity, start a focus session linked to this task first. Are you sure you want to mark it done anyway?
        </div>
      )}

      <div style={{fontSize:"14px", color:"var(--text2)", marginBottom:"16px", lineHeight:1.6}}>
        Great work on <strong style={{color:"var(--text)"}}>{completingTask.name}</strong>!
        Write a brief note about what you completed.
      </div>

      {completingTask.type === "deep" && completingTask.estimatedMins && (
        <div style={{fontSize:"13px", color:"var(--text3)", marginBottom:"12px"}}>
          ⏱ Estimated: {completingTask.estimatedMins} mins
          {completingTask.loggedMins ? ` · Logged: ${completingTask.loggedMins} mins` : " · No focus time logged"}
        </div>
      )}

      <div style={styles.formGroup}>
        <label style={styles.label}>What did you complete? (min. 30 characters)</label>
        <textarea
          style={{...styles.input, minHeight:"80px", resize:"vertical"}}
          placeholder="e.g. Finished writing the introduction and conclusion sections..."
          value={completionNote}
          onChange={e => { setCompletionNote(e.target.value); setCompletionError(""); }}
        />
        <div style={{fontSize:"11px", color: completionNote.length >= 30 ? "var(--green)" : "var(--text3)", marginTop:"4px"}}>
          {completionNote.length}/30 characters minimum
        </div>
      </div>

      {completionError && <div style={styles.errorText}>{completionError}</div>}

      <button style={styles.submitBtn} onClick={submitCompletion}>
        Complete task ✅
      </button>
      <button style={{...styles.submitBtn, background:"transparent", border:"1px solid var(--border2)", color:"var(--text2)", marginTop:"8px"}}
        onClick={() => setCompletingTask(null)}>
        Cancel
      </button>
    </div>
  </div>
)}
    </div>
  );
}

const styles = {
  tabs:          { display:"flex", gap:"6px" },
  tab:           { padding:"7px 14px", borderRadius:"20px", fontSize:"13px", fontWeight:500, border:"1px solid var(--border2)", cursor:"pointer", background:"transparent", fontFamily:"inherit", color:"var(--text2)" },
  tabActive:     { background:"var(--accent)", borderColor:"var(--accent)", color:"#fff" },
  addBtn:        { padding:"7px 16px", borderRadius:"20px", fontSize:"13px", fontWeight:600, background:"var(--accent)", border:"none", color:"#fff", cursor:"pointer", fontFamily:"inherit" },
  sLabel:        { fontSize:"11px", fontWeight:600, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".08em", margin:"16px 0 8px" },
  taskCard:      { background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"16px", padding:"13px 14px", marginBottom:"8px", display:"flex", alignItems:"flex-start", gap:"12px" },
  check:         { width:"22px", height:"22px", borderRadius:"50%", border:"1.5px solid var(--border2)", flexShrink:0, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", marginTop:"2px" },
  checkDone:     { background:"var(--green)", border:"1.5px solid var(--green)" },
  taskBody:      { flex:1, minWidth:0 },
  taskName:      { fontSize:"14px", fontWeight:500, color:"var(--text)" },
  taskDone:      { color:"var(--text3)", textDecoration:"line-through" },
  taskCourse:    { fontSize:"11px", marginTop:"3px" },
  timeEstimate:  { fontSize:"11px", color:"var(--text3)", marginTop:"3px" },
  completionNote:{ fontSize:"11px", color:"var(--green)", marginTop:"4px", lineHeight:1.4 },
  countdown:     { display:"inline-block", fontSize:"11px", fontWeight:600, padding:"2px 8px", borderRadius:"20px", marginTop:"5px" },
  typeBadge:     { fontSize:"10px", fontWeight:600, padding:"2px 7px", borderRadius:"20px", flexShrink:0 },
  typeBadgeDeep: { background:"rgba(124,110,248,.15)", color:"var(--accent2)" },
  typeBadgeQuick:{ background:"rgba(46,204,138,.15)", color:"var(--green)" },
  taskRight:     { display:"flex", alignItems:"center", gap:"4px", flexShrink:0 },
  editBtn:       { background:"none", border:"none", fontSize:"14px", cursor:"pointer", padding:"2px 4px" },
  delBtn:        { background:"none", border:"none", color:"var(--text3)", fontSize:"18px", cursor:"pointer", padding:"0 4px" },
  empty:         { textAlign:"center", padding:"48px 20px" },
  emptyIcon:     { fontSize:"36px", marginBottom:"10px" },
  emptyTitle:    { fontSize:"16px", fontWeight:600, color:"var(--text)", marginBottom:"6px" },
  emptyDesc:     { fontSize:"13px", color:"var(--text2)" },
  overlay:       { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:200 },
  modalBox:      { background:"var(--modal-bg)", borderRadius:"24px 24px 0 0", padding:"20px 20px 40px", width:"100%", maxWidth:"480px", maxHeight:"90vh", overflowY:"auto" },
  handle:        { width:"38px", height:"4px", background:"var(--surface2)", borderRadius:"2px", margin:"0 auto 18px" },
  modalTitle:    { fontSize:"18px", fontWeight:700, color:"var(--text)", marginBottom:"18px" },
  formGroup:     { marginBottom:"14px" },
  formRow:       { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" },
  label:         { fontSize:"12px", fontWeight:600, color:"var(--text2)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"6px", display:"block" },
  input:         { width:"100%", padding:"12px 14px", background:"var(--input-bg)", border:"1px solid var(--border2)", borderRadius:"8px", fontFamily:"inherit", fontSize:"14px", color:"var(--text)", outline:"none" },
  typeBtn:       { flex:1, padding:"10px", border:"1px solid var(--border2)", borderRadius:"10px", background:"transparent", fontFamily:"inherit", fontSize:"13px", fontWeight:500, color:"var(--text2)", cursor:"pointer" },
  typeBtnActive: { background:"var(--green-bg)", borderColor:"var(--green)", color:"var(--green)" },
  typeBtnActiveDeep: { background:"var(--accent-bg)", borderColor:"var(--accent)", color:"var(--accent2)" },
  submitBtn:     { width:"100%", padding:"14px", background:"var(--accent)", border:"none", borderRadius:"12px", fontFamily:"inherit", fontSize:"15px", fontWeight:600, color:"#fff", cursor:"pointer", marginTop:"4px" },
  errorText:     { fontSize:"13px", color:"var(--red)", marginBottom:"10px" },
};